import {
  OUTCOME_FIX_INFO, OUTCOME_FLAG_LABELS, outcomeCauseInfo, outcomeCauseLabel, outcomeWaitingOn,
  type OutcomeFix, type OutcomeFlag, type OutcomeOwner, type SessionOutcomeSummary, type StartAttemptView,
} from '@evi/shared';

/**
 * Session success in Admin's words (Warren, 28 Oct 2026, addendum item 2):
 * the "What happened" card on the session page, the start success card and
 * the start attempts list on the charger page. Pure, so the words are tested
 * without rendering; the vocabulary itself is @evi/shared session-outcome.ts.
 */

/** Session.outcome as the session read sends it to staff. */
export interface SessionOutcomeRead {
  kind: 'start' | 'session';
  cause: string;
  tally?: string;
  counted: boolean;
  owner: string;
  fix: string | null;
  note: string | null;
  stop_reason?: string | null;
  late_stop_reason?: string | null;
  flags?: string[];
  evidence?: Record<string, unknown> | null;
  classified_at?: string;
}

export interface WhatHappenedView {
  title: string;
  sentence: string;
  /** bad: counted against the target; muted: shown, not counted; ok: a success. */
  tone: 'ok' | 'muted' | 'bad';
  facts: Array<{ label: string; value: string }>;
  fix: string | null;
  waitingOn: string | null;
  flags: string[];
}

const FIX_STATUS_SUFFIX: Record<string, string> = {
  live: '',
  manual: ' (a person does this)',
  ladder: ' (with the automatic fixes)',
  not_built: ' (not built yet)',
};

/** A finite number, or null. */
function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}

/** 21:33:44 UTC on 28 Oct, the way every UTC time on these pages reads. */
export function utcClock(iso: unknown): string | null {
  const s = str(iso);
  if (!s) return null;
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return null;
  return `${d.toISOString().slice(11, 19)} UTC, ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })}`;
}

const STRANDED_WORDS: Record<string, string> = {
  stranded_watchdog_autoclose: 'the stranded-session closer (the charger had gone silent)',
  stranded_reconcile_charger_idle: 'the stranded-session reconciler (the charger came back idle)',
  stranded_admin_force_close: 'an admin force close',
};

const SIGNAL_WORDS: Record<string, string> = {
  network_event: 'many sites dropped at once',
  boot_burst: 'many sites rebooted at once',
  task_end: 'our server restarted',
};

/** The evidence the rule saw, in plain lines. Unknown keys are left out, never printed raw. */
export function outcomeFacts(outcome: SessionOutcomeRead): Array<{ label: string; value: string }> {
  const e = outcome.evidence ?? {};
  const out: Array<{ label: string; value: string }> = [];
  const add = (label: string, value: string | null) => { if (value) out.push({ label, value }); };
  add('Stop reason', str(outcome.stop_reason) ?? str(e['stop_reason']));
  const lastKw = num(e['last_power_kw']);
  const age = num(e['last_sample_age_s']);
  if (lastKw !== null) add('Last power', `${lastKw} kW${age !== null ? `, ${age} s before the stop` : ''}`);
  const peak = num(e['peak_power_kw']);
  if (peak !== null && outcome.kind === 'session') add('Peak power', `${peak} kW`);
  const from = num(e['from_connector_id']);
  const to = num(e['to_connector_id']);
  if (from !== null && to !== null) add('Redirect', `Tapped connector ${from}, started connector ${to}`);
  const started = num(e['started_connector_id']);
  if (started !== null) add('Started connector', String(started));
  const preparing = num(e['preparing_connector_id']);
  if (preparing !== null) {
    const at = utcClock(e['preparing_at']);
    add('Car was on', `Connector ${preparing}${at ? ` (Preparing from ${at})` : ''}`);
  }
  if (str(e['sibling_charger_id'])) add('Cable went to', `Another charger at this site${utcClock(e['preparing_at']) ? `, Preparing at ${utcClock(e['preparing_at'])}` : ''}`);
  const waited = num(e['waited_s']);
  if (waited !== null) add('Waited', `${waited} s`);
  const signal = str(e['platform_signal']);
  if (signal) add('Our platform', `${SIGNAL_WORDS[signal] ?? signal}${utcClock(e['platform_at']) ? `, ${utcClock(e['platform_at'])}` : ''}`);
  add('Start guard', str(e['guard_message']));
  const fault = [str(e['error_code']), str(e['vendor_error_code'])].filter(Boolean).join(', ');
  if (fault) add('Fault', fault);
  add('Connector status', str(e['connector_status']));
  const stranded = str(e['stranded_reason']);
  if (stranded) add('Closed by', STRANDED_WORDS[stranded] ?? stranded.replace(/_/g, ' '));
  add('Late stop frame', str(outcome.late_stop_reason) ?? str(e['late_stop_reason']));
  const http = num(e['http_status']);
  const code = str(e['code']);
  if (http !== null || code) add('Reply', [http !== null ? String(http) : null, code].filter(Boolean).join(' '));
  add('Start command', str(e['audit_outcome'])?.replace(/_/g, ' ') ?? null);
  add('Recorded end', str(e['failure_reason'])?.replace(/_/g, ' ') ?? null);
  if (e['inferred'] === true) add('Read from', 'A replay of the stored configuration (inferred)');
  return out;
}

export function fixLabel(fix: string | null): string | null {
  if (!fix || !(fix in OUTCOME_FIX_INFO)) return null;
  const info = OUTCOME_FIX_INFO[fix as OutcomeFix];
  return `${info.label}${FIX_STATUS_SUFFIX[info.status] ?? ''}`;
}

const NOTE_WORDS: Record<string, string> = {
  redirected: 'The wrong-connector redirect started the connector the car was in.',
  early_cut: 'Cut in the first 5 minutes: counted against session completion, not the start.',
};

/** The card for a staff reader (the whole outcome) or a Site (the summary). Null when the session is not classified yet. */
export function whatHappened(outcome: SessionOutcomeRead | null | undefined, summary?: SessionOutcomeSummary | null): WhatHappenedView | null {
  if (outcome && typeof outcome.cause === 'string') {
    const info = outcomeCauseInfo(outcome.cause);
    const tally = outcome.tally ?? info?.tally ?? (outcome.counted ? 'failure' : 'success');
    const note = outcome.note ? NOTE_WORDS[outcome.note] ?? null : null;
    return {
      title: info?.label ?? outcomeCauseLabel(outcome.cause),
      sentence: [info?.sentence ?? null, note].filter(Boolean).join(' '),
      tone: tally === 'failure' ? 'bad' : tally === 'excluded' ? 'muted' : 'ok',
      facts: outcomeFacts(outcome),
      fix: fixLabel(outcome.fix),
      waitingOn: tally === 'failure' ? outcomeWaitingOn(outcome.owner as OutcomeOwner, (outcome.fix as OutcomeFix | null) ?? null) : null,
      flags: (outcome.flags ?? []).map((f) => OUTCOME_FLAG_LABELS[f as OutcomeFlag] ?? f.replace(/_/g, ' ')),
    };
  }
  if (summary) {
    const info = outcomeCauseInfo(summary.cause);
    return {
      title: summary.label,
      sentence: info?.sentence && info.owner !== 'driver' ? info.sentence : '',
      tone: summary.counted ? 'bad' : 'ok',
      facts: [],
      fix: null,
      waitingOn: summary.counted ? outcomeWaitingOn(summary.owner, null) : null,
      flags: [],
    };
  }
  return null;
}

// ── The charger page ─────────────────────────────────────────────────────

/** A rate as a whole percent, never rounded up past the target: 97.6% reads 97%. */
export function percentFloor(rate: number | null): string {
  return rate === null ? '' : `${Math.floor(rate * 100 + 1e-9)}%`;
}

/** "96% · 48 of 50 started", or "no starts". */
export function formatStartSuccess(row: { counted: number; succeeded: number; rate: number | null }): string {
  if (row.counted === 0 || row.rate === null) return 'no starts';
  return `${percentFloor(row.rate)} · ${row.succeeded} of ${row.counted} started`;
}

/** The causes most often seen, in plain words: "Wrong connector 2 · Charger offline 1". */
export function topCauses(causes: Readonly<Record<string, number>>, limit = 2): string {
  return Object.entries(causes)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([cause, n]) => `${outcomeCauseLabel(cause)} ${n}`)
    .join(' · ');
}

/** "Completion 92% of 25 sessions", or '' with none. */
export function completionLabel(row: { sessions: number; completion_rate: number | null }): string {
  if (row.sessions === 0 || row.completion_rate === null) return '';
  return `Completion ${percentFloor(row.completion_rate)} of ${row.sessions} session${row.sessions === 1 ? '' : 's'}`;
}

const SOURCE_WORDS: Record<StartAttemptView['source'], string> = { app: 'App', rfid: 'Card', admin: 'Staff' };
const RESULT_WORDS: Record<StartAttemptView['result'], string> = { started: 'Started', failed: 'Failed', excluded: 'Not counted', pending: 'Waiting' };

export interface AttemptRowView {
  id: string;
  at: string;
  source: string;
  connector: string;
  result: string;
  failed: boolean;
  cause: string;
  fix: string;
  sessionId: string | null;
}

export function attemptRows(rows: readonly StartAttemptView[], failedOnly: boolean): AttemptRowView[] {
  return rows
    .filter((r) => !failedOnly || r.result === 'failed')
    .map((r) => ({
      id: r.id,
      at: utcClock(r.at) ?? r.at,
      source: SOURCE_WORDS[r.source] ?? r.source,
      connector: r.requested_connector_id !== null && r.connector_id !== null && r.requested_connector_id !== r.connector_id
        ? `${r.requested_connector_id} to ${r.connector_id}`
        : String(r.connector_id ?? r.requested_connector_id ?? ''),
      result: RESULT_WORDS[r.result] ?? r.result,
      failed: r.result === 'failed',
      cause: r.cause === 'started' ? (r.note === 'redirected' ? 'Started (redirected)' : '') : r.label ?? '',
      fix: r.result === 'failed' ? fixLabel(r.fix) ?? '' : '',
      sessionId: r.session_id,
    }));
}
